import { ref, onMounted, onBeforeUnmount, watch, unref } from 'vue'
import { MODAL_HIDE, MODAL_SHOW, modalBus } from '@/Utils/modalBus'

function resolveModalId(modalIdRef) {
	// Support getter functions (e.g. () => props.modalId), refs, and plain values
	if (typeof modalIdRef === 'function') {
		return modalIdRef()
	}
	return unref(modalIdRef)
}

/**
 * Bridges legacy modal triggers to ModalBox v-model.
 * Listens on modalBus (mitt) instead of $root.
 */
export function useLegacyModalBridge(modalIdRef, callbacks = {}) {
	const isVisible = ref(false)

	const matches = (id) => id === resolveModalId(modalIdRef)

	const onShow = (id) => {
		if (matches(id)) {
			isVisible.value = true
		}
	}

	const onHide = (id) => {
		if (matches(id)) {
			isVisible.value = false
		}
	}

	onMounted(() => {
		modalBus.on(MODAL_SHOW, onShow)
		modalBus.on(MODAL_HIDE, onHide)
	})

	onBeforeUnmount(() => {
		modalBus.off(MODAL_SHOW, onShow)
		modalBus.off(MODAL_HIDE, onHide)
	})

	watch(isVisible, (val, oldVal) => {
		if (val && !oldVal) {
			callbacks.onShow?.()
		}
		if (!val && oldVal) {
			callbacks.onHidden?.()
			callbacks.onHide?.()
		}
	})

	const show = () => {
		isVisible.value = true
	}

	const hide = () => {
		isVisible.value = false
		// Broadcast on the bus too: modals are opened globally via
		// showModal()/MODAL_SHOW (which flips every instance with this id), so
		// closing must be symmetric or stray instances stay open (stuck overlay).
		modalBus.emit(MODAL_HIDE, resolveModalId(modalIdRef))
	}

	return { isVisible, show, hide }
}

/** Options API mixin for modals that still use export default {} */
export const legacyModalBridgeMixin = {
	data() {
		return {
			legacyModalVisible: false,
		}
	},
	mounted() {
		this._onLegacyModalShow = (id) => {
			if (id === this.getLegacyModalId()) {
				this.legacyModalVisible = true
			}
		}
		this._onLegacyModalHide = (id) => {
			if (id === this.getLegacyModalId()) {
				this.legacyModalVisible = false
			}
		}

		modalBus.on(MODAL_SHOW, this._onLegacyModalShow)
		modalBus.on(MODAL_HIDE, this._onLegacyModalHide)

		this._migrateLegacyModalRefHooks?.()
	},
	beforeUnmount() {
		if (this._onLegacyModalShow) {
			modalBus.off(MODAL_SHOW, this._onLegacyModalShow)
		}
		if (this._onLegacyModalHide) {
			modalBus.off(MODAL_HIDE, this._onLegacyModalHide)
		}
	},
	watch: {
		legacyModalVisible(val, oldVal) {
			if (val && !oldVal) {
				this.onLegacyModalShow?.()
			}
			if (!val && oldVal) {
				this.onLegacyModalHidden?.()
				this.onLegacyModalHide?.()
			}
		},
	},
	methods: {
		getLegacyModalId() {
			if (typeof this.legacyModalId === 'function') {
				return this.legacyModalId()
			}
			return this.legacyModalId ?? this.$options.legacyModalId ?? this.modalId
		},
		showLegacyModal() {
			this.legacyModalVisible = true
		},
		hideLegacyModal() {
			this.legacyModalVisible = false
			// Symmetric with the global showModal()/MODAL_SHOW open: broadcast the
			// hide so every instance with this id closes (prevents stuck overlays).
			modalBus.emit(MODAL_HIDE, this.getLegacyModalId())
		},
	},
}
